import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Instagram, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { businessInfo } from '../data/mock';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServiceAreasOpen, setIsServiceAreasOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServiceAreasOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;
  const isServiceAreasActive = location.pathname.startsWith('/service-areas');

  const serviceAreaCities = [
    { name: 'Redlands, CA', path: '/service-areas/redlands-ca' },
    { name: 'Loma Linda, CA', path: '/service-areas/loma-linda-ca' },
    { name: 'Yucaipa, CA', path: '/service-areas/yucaipa-ca' },
    { name: 'Rancho Cucamonga, CA', path: '/service-areas/rancho-cucamonga-ca' },
    { name: 'Riverside, CA', path: '/service-areas/riverside-ca' },
    { name: 'San Bernardino, CA', path: '/service-areas/san-bernardino-ca' },
    { name: 'Ontario, CA', path: '/service-areas/ontario-ca' },
    { name: 'Corona, CA', path: '/service-areas/corona-ca' },
    { name: 'Fontana, CA', path: '/service-areas/fontana-ca' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-white/95 backdrop-blur-md'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_shine-mobile-14/artifacts/48ukm1vz_image.png" 
              alt="Precision Mobile Detailing Logo" 
              className="h-12 w-auto"
            />
            <a 
              href="https://instagram.com/precisiondetailing.ie" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-gray-600 hover:text-black transition-colors text-sm font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              <Instagram className="w-5 h-5" />
              @precisiondetailing.ie
            </a>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className={`font-medium transition-colors ${
                isActive('/') ? 'text-black' : 'text-gray-600 hover:text-black'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/services" 
              className={`font-medium transition-colors ${
                isActive('/services') ? 'text-black' : 'text-gray-600 hover:text-black'
              }`}
            >
              Services
            </Link>
            
            {/* Service Areas Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsServiceAreasOpen(true)}
              onMouseLeave={() => setIsServiceAreasOpen(false)}
            >
              <Link
                to="/service-areas"
                className={`font-medium transition-colors flex items-center gap-1 ${
                  isServiceAreasActive ? 'text-black' : 'text-gray-600 hover:text-black'
                }`}
              >
                Service Areas
                <ChevronDown className={`w-4 h-4 transition-transform ${isServiceAreasOpen ? 'rotate-180' : ''}`} />
              </Link>
              
              {isServiceAreasOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white shadow-xl rounded-lg border border-gray-100 py-2 z-50">
                  {serviceAreaCities.map((city) => (
                    <Link
                      key={city.path}
                      to={city.path}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-50 hover:text-black transition-colors"
                    >
                      {city.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link 
              to="/blog" 
              className={`font-medium transition-colors ${
                location.pathname.startsWith('/blog') ? 'text-black' : 'text-gray-600 hover:text-black'
              }`}
            >
              Blog
            </Link>
            
            <a 
              href={`tel:${businessInfo.phone}`} 
              className="flex items-center gap-2 font-medium text-gray-600 hover:text-black transition-colors"
            >
              <Phone className="w-4 h-4" />
              {businessInfo.phone}
            </a>
            <Button asChild className="cta-button">
              <Link to="/booking">Book Now</Link>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="container mx-auto px-6 py-6 flex flex-col gap-4">
            <Link 
              to="/" 
              className={`font-medium py-2 ${
                isActive('/') ? 'text-black' : 'text-gray-600'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>
            <Link 
              to="/services" 
              className={`font-medium py-2 ${
                isActive('/services') ? 'text-black' : 'text-gray-600'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Services
            </Link>
            
            {/* Service Areas Mobile */}
            <div>
              <button
                onClick={() => setIsServiceAreasOpen(!isServiceAreasOpen)}
                className={`font-medium py-2 flex items-center gap-2 w-full ${
                  isServiceAreasActive ? 'text-black' : 'text-gray-600'
                }`}
              >
                Service Areas
                <ChevronDown className={`w-4 h-4 transition-transform ${isServiceAreasOpen ? 'rotate-180' : ''}`} />
              </button>
              {isServiceAreasOpen && (
                <div className="pl-4 mt-2 flex flex-col gap-2">
                  {serviceAreaCities.map((city) => (
                    <Link
                      key={city.path}
                      to={city.path}
                      className="py-2 text-gray-600 hover:text-black"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {city.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            
            <Link 
              to="/blog" 
              className={`font-medium py-2 ${
                location.pathname.startsWith('/blog') ? 'text-black' : 'text-gray-600'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Blog
            </Link>
            
            <a 
              href={`tel:${businessInfo.phone}`} 
              className="flex items-center gap-2 font-medium text-gray-600 py-2"
            >
              <Phone className="w-4 h-4" />
              {businessInfo.phone}
            </a>
            <Button asChild className="cta-button w-full">
              <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)}>
                Book Now
              </Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
